<template>
  <div class="end-of-list-indicator">
    <div class="end-of-list-indicator__icon" :aria-hidden="true">{{ icon }}</div>
    <div class="end-of-list-indicator__content">
      <h4 class="end-of-list-indicator__title">{{ title }}</h4>
      <p v-if="showStats" class="end-of-list-indicator__stats">{{ statsText }}</p>
      <button 
        v-if="showBackToTop"
        @click="scrollToTop"
        class="end-of-list-indicator__back-to-top"
        type="button"
      >
        {{ backToTopText }} ↑
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'EndOfListIndicator',
  props: {
    title: {
      type: String,
      default: "You've reached the end!"
    },
    icon: {
      type: String,
      default: '🎯'
    },
    totalItems: {
      type: Number,
      default: 0
    },
    showStats: {
      type: Boolean,
      default: true
    },
    showBackToTop: {
      type: Boolean,
      default: true
    },
    backToTopText: {
      type: String,
      default: 'Back to Top'
    }
  },
  computed: {
    statsText() {
      if (this.totalItems === 0) {
        return 'No entries to display'
      } else if (this.totalItems === 1) {
        return 'Viewed 1 entry'
      } else {
        return `Viewed ${this.totalItems} entries`
      }
    }
  },
  methods: {
    scrollToTop() {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
      
      // Emit event for parent component to handle if needed
      this.$emit('back-to-top')
    }
  },
  emits: ['back-to-top']
}
</script>

<style scoped>
.end-of-list-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 32px 16px;
  margin: 24px 0;
  text-align: center;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.end-of-list-indicator__icon {
  font-size: 32px;
  opacity: 0.8;
}

.end-of-list-indicator__content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 300px;
}

.end-of-list-indicator__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
  opacity: 0.9;
}

.end-of-list-indicator__stats {
  margin: 0;
  font-size: 14px;
  color: #888;
  opacity: 0.8;
}

.end-of-list-indicator__back-to-top {
  align-self: center;
  padding: 8px 16px;
  background: rgba(78, 205, 196, 0.1);
  border: 1px solid rgba(78, 205, 196, 0.3);
  color: #4ecdc4;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 4px;
}

.end-of-list-indicator__back-to-top:hover {
  background: rgba(78, 205, 196, 0.2);
  transform: translateY(-1px);
}

.end-of-list-indicator__back-to-top:focus {
  outline: 2px solid #4ecdc4;
  outline-offset: 2px;
}

.end-of-list-indicator__back-to-top:active {
  transform: translateY(0);
}

/* Mobile responsive adjustments */
@media (max-width: 768px) {
  .end-of-list-indicator {
    flex-direction: column;
    gap: 12px;
    padding: 24px 16px;
  }
  
  .end-of-list-indicator__icon {
    font-size: 28px;
  }
  
  .end-of-list-indicator__title {
    font-size: 16px;
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .end-of-list-indicator {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  .end-of-list-indicator__title {
    color: #ffffff;
  }
  
  .end-of-list-indicator__stats {
    color: #cccccc;
  }
  
  .end-of-list-indicator__back-to-top {
    background: rgba(78, 205, 196, 0.2);
    border-color: #4ecdc4;
  }
}
</style>